import { useState } from 'react';
import type { TenantProfile, TenantSummary } from '@shared/types';
import { useAuth } from '../app/auth';
import { api, describeError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { formatDateTime } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Input,
  Modal,
  Notice,
  PageHeader,
  Pagination,
  Select,
  Table,
  Textarea,
  type Column,
} from '../components/ui';

interface TenantList {
  items: TenantSummary[];
  total: number;
  page: number;
  pageSize: number;
}

interface TenantDetail {
  tenant: {
    id: string;
    name: string;
    slug: string;
    kind: string;
    status: string;
    verificationStatus: string;
    createdAt: string;
  };
  profile: TenantProfile | null;
}

const VERIFY_TONE = {
  pending: 'warn',
  verified: 'ok',
  rejected: 'danger',
} as const;

export function TenantsPage() {
  const { can } = useAuth();
  const canReview = can('tenant.review');
  const canManage = can('customer.manage');

  const [page, setPage] = useState(1);
  const [verifyFilter, setVerifyFilter] = useState('pending');
  const [kind, setKind] = useState('');
  const [query, setQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [version, setVersion] = useState(0);

  const [detail, setDetail] = useState<TenantDetail | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const tenants = useAsync<TenantList>(() => {
    const params = new URLSearchParams({ page: String(page), pageSize: '20' });
    if (verifyFilter !== '') params.set('verificationStatus', verifyFilter);
    if (kind !== '') params.set('kind', kind);
    if (appliedQuery !== '') params.set('query', appliedQuery);
    return api.get(`/api/tenants?${params.toString()}`);
  }, [page, verifyFilter, kind, appliedQuery, version]);

  async function openDetail(tenant: TenantSummary) {
    setLoadingId(tenant.id);
    setError(null);
    setNotice(null);
    setReason('');
    try {
      setDetail(await api.get<TenantDetail>(`/api/tenants/${tenant.id}`));
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setLoadingId(null);
    }
  }

  async function review(approved: boolean) {
    if (!detail) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.post(`/api/tenants/${detail.tenant.id}/verification`, {
        approved,
        reason: approved ? null : reason.trim() || '资料不完整',
      });
      setNotice(approved ? '已通过审核' : '已驳回');
      setVersion((value) => value + 1);
      setDetail(await api.get<TenantDetail>(`/api/tenants/${detail.tenant.id}`));
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(status: 'active' | 'suspended') {
    if (!detail) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/tenants/${detail.tenant.id}/status`, { status });
      setNotice(status === 'suspended' ? '组织已停用' : '组织已启用');
      setVersion((value) => value + 1);
      setDetail(await api.get<TenantDetail>(`/api/tenants/${detail.tenant.id}`));
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setBusy(false);
    }
  }

  const columns: Column<TenantSummary>[] = [
    {
      key: 'name',
      header: '组织',
      render: (row) => (
        <div>
          <p className="text-fg">{row.name}</p>
          <p className="font-mono text-xs text-fg-subtle">
            {row.slug} · {row.id}
          </p>
        </div>
      ),
    },
    {
      key: 'kind',
      header: '类型',
      render: (row) => (
        <Badge tone={row.kind === 'enterprise' ? 'brand' : 'neutral'}>
          {row.kind === 'enterprise' ? '企业' : '个人'}
        </Badge>
      ),
    },
    {
      key: 'verify',
      header: '资质',
      render: (row) => (
        <Badge tone={VERIFY_TONE[row.verificationStatus]}>{row.verificationStatus}</Badge>
      ),
    },
    {
      key: 'owner',
      header: '所有者',
      render: (row) => <span className="text-xs text-fg-muted">{row.ownerEmail ?? '—'}</span>,
    },
    {
      key: 'members',
      header: '成员',
      render: (row) => <span className="tabular">{row.memberCount}</span>,
    },
    {
      key: 'created',
      header: '创建时间',
      render: (row) => <span className="text-xs text-fg-muted">{formatDateTime(row.createdAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      width: '90px',
      render: (row) => (
        <Button size="sm" loading={loadingId === row.id} onClick={() => void openDetail(row)}>
          详情
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="组织与审核"
        description="企业组织注册后为 pending；审核通过前不能邀请成员、生成 API Key 或开票。"
      />

      {error && detail === null ? <Notice tone="danger">{error}</Notice> : null}

      <Card>
        <CardHeader title="组织列表" hint={`${tenants.data?.total ?? 0} 个组织`} />
        <div className="mb-3 flex flex-wrap gap-2">
          <div className="w-40">
            <Select
              value={verifyFilter}
              onChange={(value) => {
                setVerifyFilter(value);
                setPage(1);
              }}
              options={[
                { value: 'pending', label: '待审核' },
                { value: '', label: '全部资质' },
                { value: 'verified', label: '已通过' },
                { value: 'rejected', label: '已驳回' },
              ]}
            />
          </div>
          <div className="w-36">
            <Select
              value={kind}
              onChange={(value) => {
                setKind(value);
                setPage(1);
              }}
              options={[
                { value: '', label: '全部类型' },
                { value: 'personal', label: '个人' },
                { value: 'enterprise', label: '企业' },
              ]}
            />
          </div>
          <div className="w-64">
            <Input
              value={query}
              onChange={setQuery}
              placeholder="组织名 / slug / ID"
              onEnter={() => {
                setAppliedQuery(query.trim());
                setPage(1);
              }}
            />
          </div>
          <Button
            onClick={() => {
              setAppliedQuery(query.trim());
              setPage(1);
            }}
          >
            搜索
          </Button>
        </div>

        <Table
          columns={columns}
          rows={tenants.data?.items ?? []}
          getKey={(row) => row.id}
          loading={tenants.loading}
          empty="没有符合条件的组织"
        />
        <Pagination
          page={page}
          pageSize={tenants.data?.pageSize ?? 20}
          total={tenants.data?.total ?? 0}
          onChange={setPage}
        />
      </Card>

      <Modal
        open={detail !== null}
        title="组织详情"
        onClose={() => setDetail(null)}
        width="max-w-2xl"
        footer={<Button onClick={() => setDetail(null)}>关闭</Button>}
      >
        {detail ? (
          <div className="space-y-4">
            {error ? <Notice tone="danger">{error}</Notice> : null}
            {notice ? <Notice tone="ok">{notice}</Notice> : null}

            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
              <div>
                <dt className="text-fg-muted">组织名</dt>
                <dd className="text-fg">{detail.tenant.name}</dd>
              </div>
              <div>
                <dt className="text-fg-muted">标识</dt>
                <dd className="font-mono text-xs text-fg">{detail.tenant.slug}</dd>
              </div>
              <div>
                <dt className="text-fg-muted">类型</dt>
                <dd className="text-fg">{detail.tenant.kind}</dd>
              </div>
              <div>
                <dt className="text-fg-muted">资质状态</dt>
                <dd>
                  <Badge
                    tone={
                      VERIFY_TONE[
                        detail.tenant.verificationStatus as keyof typeof VERIFY_TONE
                      ] ?? 'neutral'
                    }
                  >
                    {detail.tenant.verificationStatus}
                  </Badge>
                </dd>
              </div>
              <div>
                <dt className="text-fg-muted">状态</dt>
                <dd className="text-fg">{detail.tenant.status}</dd>
              </div>
              <div>
                <dt className="text-fg-muted">创建时间</dt>
                <dd className="text-fg">{formatDateTime(detail.tenant.createdAt)}</dd>
              </div>
            </dl>

            {detail.profile ? (
              <div className="rounded-md bg-canvas p-3 text-[13px]">
                <p className="mb-1 font-medium text-fg">企业资料</p>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <div>
                    <dt className="text-fg-muted">企业名称</dt>
                    <dd className="text-fg">{detail.profile.legalName}</dd>
                  </div>
                  <div>
                    <dt className="text-fg-muted">联系人</dt>
                    <dd className="text-fg">
                      {detail.profile.contactName} · {detail.profile.contactPhone}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-fg-muted">行业</dt>
                    <dd className="text-fg">{detail.profile.industry ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-fg-muted">网址</dt>
                    <dd className="truncate text-fg">{detail.profile.website ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-fg-muted">营业执照号</dt>
                    <dd className="text-fg">{detail.profile.licenseNo ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-fg-muted">上次审核</dt>
                    <dd className="text-fg">{formatDateTime(detail.profile.reviewedAt)}</dd>
                  </div>
                </dl>
                {detail.profile.rejectReason ? (
                  <p className="mt-2 text-xs text-danger">驳回原因：{detail.profile.rejectReason}</p>
                ) : null}
              </div>
            ) : (
              <Notice tone="info">该组织还没有提交企业资料。</Notice>
            )}

            {canReview && detail.tenant.kind === 'enterprise' ? (
              <div className="space-y-2 border-t border-line pt-3">
                <p className="text-[13px] font-medium text-fg">资质审核</p>
                <Textarea
                  value={reason}
                  onChange={setReason}
                  rows={2}
                  placeholder="驳回时填写原因（会展示给客户）"
                />
                <div className="flex gap-2">
                  <Button variant="primary" loading={busy} onClick={() => void review(true)}>
                    通过
                  </Button>
                  <Button variant="danger" loading={busy} onClick={() => void review(false)}>
                    驳回
                  </Button>
                </div>
              </div>
            ) : null}

            {canManage ? (
              <div className="flex gap-2 border-t border-line pt-3">
                <Button
                  loading={busy}
                  onClick={() =>
                    void setStatus(detail.tenant.status === 'active' ? 'suspended' : 'active')
                  }
                >
                  {detail.tenant.status === 'active' ? '停用组织' : '启用组织'}
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
